import { Inject, Injectable, Injector } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { map, withLatestFrom, switchMap, distinctUntilChanged, first, shareReplay, startWith, mergeMap } from 'rxjs/operators';
import {
  GetCertifiedPlantByPlantIdQuery,
  OnConnextAPISubscription,
  SystemsService,
  isNotNullOrUndefined,
  StateService,
  PlantEmsEnumType,
  ProductModelService,
  AzzurroHubDataType,
  VirtualRTStateType,
  normalizeVirtualRTStatus,
  getRemainingMs,
  resolveRTMonitoringInfo,
} from 'ngx-core-services';
import { cloneDeep, isEqual } from 'lodash';
import gql from 'graphql-tag';
import { Observable, Subscription } from 'rxjs';
import { Action, Store } from '@ngrx/store';

import { BlockStateService } from '../../lib/block-state/block-state.service';
import { defaultConnextDataQuery } from '../../lib/block-state/query-constructor.query';
import { AppState, forceConnextUpdate, forceVirtualSystemUpdate, getConnextDataFromConnextCertifiedPlant, SystemEvent } from '..';
import { updateCurrentObservers, setSystemSubscribed, setSystemUnsubscribed, requestToAddASystem, addSystem, checkSystemMetadata, updateVirtualRTStatus } from '../actions/system.actions';
import { SystemDefinition } from '../state/system.state';
import { selectSystemById } from '../selectors/system.selectors';
import { updateConnextData } from '../actions/connext.actions';
import { checkAndUpdateSystemDefinitionIfNecessaryWithSystem } from '../reducers/system.reducer';
import { myCertifiedFragment } from './system-aws.query';
import { updateAzzurroHubData } from '../actions/azzurroHub.actions';
import { resumeRealTimeTelemetry } from '../actions/real-time.actions';
import { selectRealTimeTelemetryRuntimeActiveBySystemId } from '../selectors/real-time.selectors';

const RT_DEBUG = false;
const rtLog = (...args: any[]) => { if (RT_DEBUG) { console.log(...args); } };

const AH_SUB_DEBUG = false;
const ahLog = (...args: any[]) => { if (AH_SUB_DEBUG) { console.log('[AH-SUB]', ...args); } };

@Injectable()
export class SystemSubscriptionEffects {
  /**
   * this is used only to be able to unsubscribe
   */
  private subscriptions: Record<string, Subscription | undefined | null> = {};

  updateObserverCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateCurrentObservers, requestToAddASystem, addSystem),
      withLatestFrom(this.store),
      mergeMap(async ([action, { systemState }]) => {
        const systemId = action.systemId;
        const system = systemState.systemList[systemId];
        const observers = systemState.observers[systemId];
        const observerCount = Object.keys(observers ?? {}).length;
        let withAutomaticSubscription = true;
        if (action.type === requestToAddASystem.type || action.type === addSystem.type) {
          withAutomaticSubscription = action.withAutomaticSubscription;
        }

        if (!system) {
          return null;
        }

        const isSubscribed = system?.subscription === 'subscribed' || this.subscriptions[systemId] !== undefined;
        if (withAutomaticSubscription && !isSubscribed && observerCount > 0) {
          /** Subscription is pending -> null */
          this.subscriptions[systemId] = null;
          return await this.addSubscription(system);
        }

        return null;
      }),
      isNotNullOrUndefined(),
    )
  );

  handleUnsubscribed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateCurrentObservers),
      withLatestFrom(this.store),
      map(([action, { systemState }]) => {
        const systemId = action.systemId;
        const system = systemState.systemList[systemId];
        const observers = systemState.observers[systemId];
        const observerCount = Object.keys(observers ?? {}).length;

        if (observerCount === 0 && system.subscription !== 'unsubscribed') {
          this.subscriptions[systemId]?.unsubscribe();
          this.subscriptions[systemId] = undefined;
          return setSystemUnsubscribed({ systemId });
        }

        return null;
      }),
      isNotNullOrUndefined(),
    )
  );

  constructor(
    @Inject(Injector) private readonly injector: Injector,
    private actions$: Actions,
    private readonly blockStateService: BlockStateService,
    private readonly getMyCertifiedPlantByPlantIdQuery: GetCertifiedPlantByPlantIdQuery,
    private readonly onConnextAPISubscription: OnConnextAPISubscription,
    private readonly stateService: StateService,
    private readonly systemsService: SystemsService,
    private store: Store<AppState>,
  ) {
    this.addSubscription = this.addSubscription.bind(this);
    this.addSubscriptionWithAws = this.addSubscriptionWithAws.bind(this);
    this.addSubscriptionWithFirebase = this.addSubscriptionWithFirebase.bind(this);
    this.getConnextEvent$ = this.getConnextEvent$.bind(this);
    this.getSystemChanges$ = this.getSystemChanges$.bind(this);
    this.handleSystemEvent = this.handleSystemEvent.bind(this);
    this.handleSystemEventWithAws = this.handleSystemEventWithAws.bind(this);
    this.handleSystemEventWithFirebase = this.handleSystemEventWithFirebase.bind(this);
    this.resetSubscription = this.resetSubscription.bind(this);
  }

  handleSystemEvent(system: SystemDefinition): Observable<SystemEvent> {
    const source = system.source ?? 'firebase';
    return source === 'firebase' ? this.handleSystemEventWithFirebase(system) : this.handleSystemEventWithAws(system);
  }

  handleSystemEventWithFirebase(system: SystemDefinition): Observable<SystemEvent> {
    const systemChanges$ = this.getSystemChanges$(system);
    const connextUid = system.configuration.connextId;

    if (connextUid) {
      return this.getConnextEvent$(systemChanges$, gql`
        fragment connextapires on ConnexTAPIRes {
          deviceId
        }`);
    }

    return systemChanges$;
  }

  handleSystemEventWithAws(system: SystemDefinition): Observable<SystemEvent> {
    return this.getSystemChanges$(system);
  }

  getSystemChanges$(system: SystemDefinition): Observable<SystemEvent> {
    return this.systemsService.systems$.pipe(
      isNotNullOrUndefined(),
      map((systems) => systems.find((s) => s.uid === system.uid)),
      isNotNullOrUndefined(),
      // Some providers mutate the same system object reference in place.
      // Clone before distinctUntilChanged so status transitions (e.g. virtualRTStatus.active)
      // are compared against a stable snapshot and always propagate to store/UI.
      map((systemSnapshot) => cloneDeep(systemSnapshot)),
      distinctUntilChanged((prev, curr) => isEqual(prev, curr)),
      map((system) => forceVirtualSystemUpdate(system)),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  getConnextEvent$(systemChanges$: Observable<SystemEvent>, responseQuery: any): Observable<SystemEvent> {
    const config = (observer: any, system: any) => ({
      next: () => observer.next(forceConnextUpdate(system)),
      error: (error: any) => observer.error(error),
      complete: () => observer.complete(),
    });
    return systemChanges$.pipe(
      switchMap(({ system }) =>
        this.onConnextAPISubscription.execute(responseQuery, { deviceId: system.configuration.connextId }).pipe(
          first(),
          switchMap((d: any): Observable<SystemEvent> => new Observable<SystemEvent>(observer => {
            d.subscribe(config(observer, system));
          })),
          startWith(forceVirtualSystemUpdate(system)),
        ),
      ),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  async addSubscription(_system: SystemDefinition): Promise<Action> {
    const source = _system.source ?? 'firebase';
    rtLog(`[SystemSubscriptionEffects] addSubscription: uid=${_system.uid}, source=${source}`);
    try {
      return source === 'firebase'
        ? await this.addSubscriptionWithFirebase(_system)
        : await this.addSubscriptionWithAws(_system);
    } catch (err) {
      console.error(`[SystemSubscriptionEffects] addSubscription FAILED for uid=${_system.uid}:`, err);
      throw err;
    }
  }

  async addSubscriptionWithFirebase(_system: SystemDefinition): Promise<Action> {
    const systemId = _system.uid!;

    if (_system.configuration.connextId) {
      const connextId = _system.configuration.connextId;
      const forceFirstTime = true;
      this.blockStateService.getConnextDetails(connextId, defaultConnextDataQuery, forceFirstTime).then((connextData) => {
        this.store.dispatch(updateConnextData({ systemId, changes: connextData }));
      });
    }

    this.subscriptions[systemId] = this.handleSystemEvent(_system).subscribe(async ({ forceUpdateSystem, forceUpdateConnext, system }) => {
      const storeSystem = await this.store.select(selectSystemById(systemId)).pipe(first()).toPromise();
      system = await this.stateService.getCurrentSystemByFirebase(systemId) ?? system;
      if (forceUpdateSystem) {
        checkAndUpdateSystemDefinitionIfNecessaryWithSystem(storeSystem, system, this.store);
      }

      const isConnextDataMissing = system.configuration.connextId && !storeSystem?.data;
      if (forceUpdateConnext || isConnextDataMissing) {
        const connextId = system.configuration.connextId!;
        const connextData = await this.blockStateService.getConnextDetails(connextId, defaultConnextDataQuery, forceUpdateConnext);
        this.store.dispatch(updateConnextData({ systemId, changes: connextData }));
      }
      this.store.dispatch(checkSystemMetadata({ systemId }));
    });

    return setSystemSubscribed({ systemId });
  }

  async addSubscriptionWithAws(_system: SystemDefinition): Promise<Action> {
    const user = await this.stateService.user$.pipe(first()).toPromise();
    const systemId = _system.uid!;
    rtLog(`[SystemSubscriptionEffects] addSubscriptionWithAws: systemId=${systemId}`);

    const onlyFetch = true;
    const { data: { getCertifiedPlantByPlantId: awsSystem } } = await this.getMyCertifiedPlantByPlantIdQuery.execute(myCertifiedFragment, {
      plantId: systemId,
    }, onlyFetch);
    rtLog(`[SystemSubscriptionEffects] AWS system fetched for ${systemId}:`, awsSystem ? 'OK' : 'NULL');
    this.stateService.setSystemOnAwsRawData$(awsSystem);

    this.subscriptions[systemId] = this.handleSystemEvent(_system).subscribe(async ({ forceUpdateSystem, forceUpdateConnext, system }) => {
      const storeSystem = await this.store.select(selectSystemById(systemId)).pipe(first()).toPromise();
      if (forceUpdateSystem) {
        checkAndUpdateSystemDefinitionIfNecessaryWithSystem(storeSystem, system, this.store);
      }

      const hasConnext = system.aws?.ems?.find((ems) => ems.type === PlantEmsEnumType.CONNEXT);

      const isConnextDataMissing = system.configuration.connextId && !storeSystem?.data;
      if ((forceUpdateConnext || isConnextDataMissing) && hasConnext) {
        const connextData = getConnextDataFromConnextCertifiedPlant(system, user!);
        this.store.dispatch(updateConnextData({ systemId, changes: connextData }));
      }

      const hasAzzurroHub = system.aws?.ems?.find((ems) => ems.type === PlantEmsEnumType.AZZURRO_HUB);
      const isAzzurroHubDataMissing = system.configuration.azzurroHubId && !storeSystem?.data;

      if ((forceUpdateConnext || isAzzurroHubDataMissing) && hasAzzurroHub) {
        const currentAzzurroHub = system.aws?.ems_state_v2?.find((ems) => {
            const id = ems.loggerId;
            const product = new ProductModelService(id);
            return product.type?.name === 'azzurro-hub';
          }) as AzzurroHubDataType;
        if (currentAzzurroHub) {
          ahLog('AzzurroHub data update:', JSON.stringify({
            loggerId: currentAzzurroHub.loggerId,
            operational: (currentAzzurroHub as any)?.config?.operational,
            connection: currentAzzurroHub.info?.connection?.status,
            inverters: currentAzzurroHub.inverter?.map((i: any) => ({
              sn: i.sn, connected: i.connected, updating: i.updating,
              arm: i.arm, dspm: i.dspm, dsps: i.dsps,
              upd_arm: i.upd_arm, upd_dspm: i.upd_dspm, upd_dsps: i.upd_dsps, upd_sofar: i.upd_sofar,
            })),
          }));
        }
        this.store.dispatch(updateAzzurroHubData({ systemId , changes: currentAzzurroHub }))
      }

      // Auto-resume RT also on live system updates, not only on initial snapshot.
      await this.tryAutoResumeRT(system, systemId);
      this.store.dispatch(checkSystemMetadata({ systemId }));
    });

    await this.tryAutoResumeRT(awsSystem as unknown as SystemDefinition, systemId, _system);

    return setSystemSubscribed({ systemId });
  }


  /**
   * Try to auto-resume an RT session from virtualRTStatus found in a system snapshot or stream update.
   * Dispatches `updateVirtualRTStatus` when status is found, and `resumeRealTimeTelemetry` when the session is still active.
   *
   * @param systemLike The system data (from stream or initial fetch) containing ems_state_v2
   * @param systemId The systemId in the store
   * @param monitoringInfoSource Optional: use a different object to resolve monitoring info (e.g. _system from store)
   */
  private async tryAutoResumeRT(
    systemLike: Partial<SystemDefinition> | null | undefined,
    systemId: string,
    monitoringInfoSource?: Partial<SystemDefinition> | null,
  ): Promise<void> {
    const rtInfo = resolveRTMonitoringInfo(monitoringInfoSource ?? systemLike);
    if (!rtInfo) {
      return;
    }

    const rawStatus = this.extractVirtualRTStatus(systemLike, rtInfo.emsId);
    if (!rawStatus) {
      return;
    }

    const normalized = normalizeVirtualRTStatus(rawStatus, systemId);
    this.store.dispatch(updateVirtualRTStatus({ systemId, virtualRTStatus: normalized }));

    const isRTRuntimeActive = await this.isRTRuntimeActive(systemId);
    if (normalized.active && !isRTRuntimeActive) {
      const remaining = getRemainingMs(normalized);
      if (remaining > 0) {
        rtLog(`[SystemSubscriptionEffects] Auto-resuming RT for system=${systemId}, remaining=${Math.round(remaining / 1000)}s`);
        this.store.dispatch(resumeRealTimeTelemetry({
          plantId: systemId,
          systemId,
          emsId: rtInfo.emsId,
          monitoringType: rtInfo.monitoringType,
          virtualRTStatus: normalized,
        }));
      }
    }
  }

  private extractVirtualRTStatus(
    systemLike: Partial<SystemDefinition> | null | undefined,
    emsId?: string,
  ): VirtualRTStateType | undefined {
    const normalizedEmsId = emsId?.toUpperCase();
    const emsState = (systemLike as any)?.aws?.ems_state_v2 ?? (systemLike as any)?.ems_state_v2 ?? [];

    if (!normalizedEmsId) {
      return undefined;
    }

    const currentEms = emsState.find((ems: any) => ems?.loggerId?.toUpperCase() === normalizedEmsId);
    return currentEms?.config?.virtualRTStatus as VirtualRTStateType | undefined;
  }

  private async isRTRuntimeActive(systemId: string): Promise<boolean> {
    return await this.store.select(selectRealTimeTelemetryRuntimeActiveBySystemId(systemId)).pipe(first()).toPromise();
  }

  async resetSubscription(system: SystemDefinition): Promise<any> {
    if (!system.uid) {
      throw new Error('System must have a uid to be subscribed');
    }

    if (this.subscriptions[system.uid]) {
      this.subscriptions[system.uid]?.unsubscribe();
      this.subscriptions[system.uid] = undefined;
    }

    return await this.addSubscription(system);
  }
}
